import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Masthead, { MastheadTitle } from '@amzn/meridian/masthead';
import Row from '@amzn/meridian/row';
import styled from '@emotion/styled';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { APPLICATION_NAME, NEWS_ARTICLES, PO_CREATOR_ROLES, locales } from '../../constants';
import { usePdfClaimsFeature } from '../../hooks/usePdfClaimsFeature';
import { useReturnTrackingFeature } from '../../hooks/useReturnTrackingFeature';
import { useUnreleasedFeatures } from '../../hooks/useUnreleasedFeatures';
import { getAuthorizedResellers, getResellerDetails } from '../../redux/actions/resellerActionCreators';
import { RootState } from '../../redux/types';
import { IMenuItem, SiteMapPage } from '../../types';
import { useSiteMapRouter } from '../../utils/SiteMapRouter';
import { Clickable, Text, TranslatedString, useTranslation, VerticalDivider } from '../blocks';
import { BadgeBox, RPMenu, UserBox } from '../composites';
import { Language } from '../icons';
import { Delight } from '.';

const Logo = styled.img`
    width: 30px;
    margin-right: 10em;
`;

const ApplicationName = styled.div`
    margin-right: 10em;
`;

const ApplicationSubName = styled(Text)`
    margin-right: 10em;
    font-weight: 200;
`;

const ApplicationRow = styled(Row)`
    display: inline-flex;
`;

type NavbarProps = ConnectedProps<typeof connector>;

// eslint-disable-next-line max-lines-per-function
const Navbar = ({
    user,
    applicationImgUrl,
    applicationSubName,
    notificationCount,
    selectedReseller,
    authorizedResellers,
    resellerDetails,
    getResellerDetails,
    getAuthorizedResellers,
}: NavbarProps) => {
    const { t } = useTranslation(['pageTitles', 'languages', 'newsPage']);
    const adminRoles = ['amazonAdmin', 'resellerAdmin', 'poAdmin'];
    const claimsRoles = [
        'amazonAdmin',
        'claimViewer',
        'claimCreator',
        'amazonCreator',
        'resellerAdmin',
        'pdfClaimAdmin',
        'pdfClaimCreator',
    ];
    const returnsRoles = ['amazonAdmin', 'amazonCreator', 'resellerAdmin', 'resellerCreator', 'resellerViewer'];
    const certificatesRoles = ['amazonAdmin', 'amazonCreator', 'certificateAdmin', 'resellerAdmin'];
    const poRoles = ['amazonAdmin', 'poViewer', 'poAdmin', 'poEditor'];
    const isPOCreator = _.intersection(user?.roles, PO_CREATOR_ROLES).length > 0;
    const { isPdfClaimEnabled } = usePdfClaimsFeature();
    const { isUnreleasedFeaturesEnabled } = useUnreleasedFeatures();
    const { isReturnTrackingEnabled } = useReturnTrackingFeature();
    const isIndirectRetailer = selectedReseller?.value?.resellerType === 'Indirect Retailer';

    const newNews = () => {
        return NEWS_ARTICLES.map((article) => {
            const currDateTime = new Date().getTime();
            const inDateWindow = article.startDate < currDateTime && currDateTime < article.endDate;
            const passesFeature = !article.requiresReturnTracking || isReturnTrackingEnabled;
            return inDateWindow && passesFeature;
        }).filter((value) => value === true).length;
    };

    useEffect(() => {
        if (resellerDetails?.status === 'Uninitialized' && selectedReseller?.value) {
            getResellerDetails(selectedReseller.value.resellerId);
        }
    }, [resellerDetails, selectedReseller, getResellerDetails]);

    useEffect(() => {
        (async () => {
            if (authorizedResellers.status === 'Uninitialized') {
                getAuthorizedResellers();
            }
        })();
    }, [authorizedResellers, getAuthorizedResellers]);

    const menu: IMenuItem[] = [
        {
            linkId: 'admin',
            path: '/admin',
            linkTitle: t('admin'),
            icon: caretDownTokens,
            dropdown: [
                {
                    linkId: 'createUser',
                    path: '/admin/create/users',
                    linkTitle: t('createUser'),
                },
                {
                    linkId: 'userList',
                    path: '/admin/list/users',
                    linkTitle: t('userList'),
                },
            ],
        },
        {
            linkId: 'po-status',
            path: '/po-status',
            linkTitle: t('purchaseOrders'),
            icon: caretDownTokens,
            dropdown: [
                {
                    linkId: 'viewPurchaseOrders',
                    path: '/po-status/list/purchase-orders',
                    linkTitle: t('viewPurchaseOrders'),
                },
                ...(isPOCreator
                    ? [
                          {
                              linkId: 'uploadPurchaseOrders',
                              path: '/po-status/upload/purchase-orders',
                              linkTitle: t('uploadPurchaseOrders'),
                          },
                      ]
                    : []),
                ...(isUnreleasedFeaturesEnabled
                    ? [
                          {
                              linkId: 'viewExtractions',
                              path: '/po-status/list/extractions',
                              linkTitle: t('viewExtractions'),
                          },
                      ]
                    : []),
            ],
        },
        {
            linkId: 'returns',
            path: '/returns',
            linkTitle: t('returns'),
            icon: caretDownTokens,
            dropdown: [
                ...(!isIndirectRetailer
                    ? [
                          {
                              linkId: 'createReturn',
                              path: '/returns/create/return-orders',
                              linkTitle: t('createReturn'),
                          },
                          {
                              linkId: 'uploadReturn',
                              path: '/returns/upload/return-orders',
                              linkTitle: t('uploadReturn'),
                          },
                      ]
                    : []),
                {
                    linkId: 'viewReturns',
                    path: '/returns/list/return-orders',
                    linkTitle: t('viewReturns'),
                },
                ...(isReturnTrackingEnabled
                    ? [
                          {
                              linkId: 'returnTracking',
                              path: '/returns/enter-tracking-info',
                              linkTitle: t('returnTracking'),
                          },
                      ]
                    : []),
            ],
        },
        {
            linkId: 'claims',
            path: '/claims',
            linkTitle: t('claims'),
            icon: caretDownTokens,
            dropdown: [
                {
                    linkId: 'uploadClaim',
                    path: '/claims/upload/claims',
                    linkTitle: t('uploadClaim'),
                },
                {
                    linkId: 'viewClaims',
                    path: '/claims/view/claims',
                    linkTitle: t('viewClaims'),
                },
                ...(isPdfClaimEnabled
                    ? [
                          {
                              linkId: 'viewPdfClaims',
                              path: '/claims/view-pdf/claims',
                              linkTitle: t('viewPdfClaims'),
                          },
                      ]
                    : []),
                ...(user && _.intersection(user.roles, ['amazonAdmin']).length > 0
                    ? [
                          {
                              linkId: 'resellerClaimRegex',
                              path: '/claims/view/reseller-regex',
                              linkTitle: t('resellerClaimRegex'),
                          },
                      ]
                    : []),
            ],
        },
        {
            linkId: 'certificates',
            path: '/certificates',
            linkTitle: t('certificates'),
            icon: caretDownTokens,
            dropdown: [
                {
                    linkId: 'uploadCertificate',
                    path: '/certificates/upload/certificates',
                    linkTitle: t('uploadCertificate'),
                },
                {
                    linkId: 'viewCertificates',
                    path: '/certificates/view/certificates',
                    linkTitle: t('viewCertificates'),
                },
            ],
        },
        {
            linkId: 'notifications',
            path: '/notifications/list/notifications',
            linkTitle: t('notifications'),
        },
        {
            linkId: 'news',
            path: '/news/list/news',
            linkTitle: t('news'),
        },
        {
            linkId: 'user',
            path: '/user',
            linkTitle: t('user'),
            dropdown: [
                {
                    linkId: 'signOut',
                    path: '/account/sign-out',
                    linkTitle: t('signOut'),
                },
                {
                    linkId: 'changePassword',
                    path: '/account/change-password',
                    linkTitle: t('changePassword'),
                },
            ],
        },
        {
            linkId: 'changeLanguage',
            path: '/account/change-language',
            icon: Language,
            dropdown: locales.map((key) => {
                return {
                    linkId: key,
                    path: `/account/change-language/${key}`,
                    linkTitle: t('languages:locale', { context: key }),
                };
            }),
        },
    ];

    const getResellerDropdownOptions = () => {
        if (authorizedResellers.status === 'Uninitialized' || authorizedResellers.status === 'Loading') {
            return [
                {
                    linkId: 'selectResellerLoading',
                    path: '/account/change-reseller',
                    linkTitle: (
                        <Row spacing={'small'}>
                            <Column>{t('selectReseller')}</Column>
                            <Column>
                                <Loader size={'small'} />
                            </Column>
                        </Row>
                    ) as any,
                    disabled: true,
                },
            ];
        } else if ((authorizedResellers?.value?.length ?? 0) > 1) {
            return [
                {
                    linkId: 'selectReseller',
                    path: '/account/change-reseller',
                    linkTitle: t('selectReseller'),
                },
            ];
        } else {
            return [];
        }
    };

    const resellerDropdownItem: IMenuItem = {
        linkId: 'reseller',
        path: '/reseller',
        linkTitle: (selectedReseller?.value?.name ?? applicationSubName) as TranslatedString,
        icon: caretDownTokens,
        dropdown: [
            ...(_.intersection(user?.roles, adminRoles).length > 0
                ? [
                      {
                          linkId: 'createContact',
                          path: '/reseller/create/contacts',
                          linkTitle: t('createContact'),
                      },
                  ]
                : []),
            {
                linkId: 'contactList',
                path: '/reseller/list/contacts',
                linkTitle: t('viewContacts'),
            },
            ...getResellerDropdownOptions(),
        ],
    };

    const determineCurrentTab = () => {
        if (window.location.pathname.startsWith(resellerDropdownItem.path)) {
            return resellerDropdownItem;
        }
        return menu.filter((i) => window.location.pathname.startsWith(i.path))[0] || menu[0];
    };

    const [currentTab, setCurrentTab] = useState(determineCurrentTab());
    const { goto } = useSiteMapRouter();

    const updateCurrentTab = () => {
        setCurrentTab(determineCurrentTab());
    };

    const onMastheadClick = () => {
        if (authorizedResellers.value?.length === 1) {
            goto(SiteMapPage.home);
        } else {
            goto(SiteMapPage.changeReseller);
        }

        updateCurrentTab();
    };

    return (
        <Masthead size={'medium'} aria-label={t('navigation')}>
            <MastheadTitle>
                <Row>
                    <Clickable onClick={onMastheadClick} data-testid={'navBar.masthead'}>
                        <Row>
                            <Logo src={applicationImgUrl} alt={'Navigate Home'} />
                            <ApplicationName>{APPLICATION_NAME}</ApplicationName>
                        </Row>
                    </Clickable>
                    <VerticalDivider />
                    <Clickable key={resellerDropdownItem.linkId} onClick={updateCurrentTab}>
                        <RPMenu
                            key={`${resellerDropdownItem.linkId}RPMenu`}
                            isSelected={currentTab.linkId === resellerDropdownItem.linkId}
                            includePadding={false}
                            item={resellerDropdownItem}
                        >
                            <ApplicationRow>
                                <ApplicationSubName data-testid={'navBar.resellerDropdown'}>
                                    {(selectedReseller?.value?.name ?? applicationSubName) as TranslatedString}
                                </ApplicationSubName>
                            </ApplicationRow>
                        </RPMenu>
                    </Clickable>
                </Row>
            </MastheadTitle>
            {menu.map((item) => {
                // FIXME: placeholder for hiding admin tab until SiteMap refactor -- 3/18/22
                if (item.linkId === 'admin' && user && _.intersection(user.roles, adminRoles).length === 0) {
                    return null;
                }
                // Hide claims roles until they are ready for external use
                if (item.linkId === 'claims' && user && _.intersection(user.roles, claimsRoles).length === 0) {
                    return null;
                }

                if (
                    item.linkId === 'certificates' &&
                    user &&
                    _.intersection(user.roles, certificatesRoles).length === 0
                ) {
                    return null;
                }
                if (item.linkId === 'returns' && user && _.intersection(user.roles, returnsRoles).length === 0) {
                    return null;
                }
                if (item.linkId === 'po-status' && user && _.intersection(user.roles, poRoles).length === 0) {
                    return null;
                }
                let specialItemContent = null;
                if (item.linkId === 'notifications' && notificationCount > 0) {
                    specialItemContent = <BadgeBox label={t('notifications')} count={notificationCount} />;
                } else if (item.linkId === 'user' && user) {
                    specialItemContent = <UserBox user={user} dataTestId={'navBar.userBox'} />;
                } else if (item.linkId === 'news') {
                    specialItemContent = <BadgeBox label={t('news')} count={newNews()} />;
                }
                return (
                    <Clickable key={item.linkId} onClick={updateCurrentTab}>
                        <RPMenu
                            key={`${item.linkId}RPMenu`}
                            isSelected={currentTab.linkId === item.linkId}
                            includePadding={true}
                            item={item}
                        >
                            {specialItemContent}
                        </RPMenu>
                    </Clickable>
                );
            })}
            {user && selectedReseller.value && (
                <Delight
                    username={user.username}
                    resellerId={selectedReseller.value.resellerId}
                    dataTestId={'delight'}
                />
            )}
        </Masthead>
    );
};

const mapStateToProps = ({ systemReducer, userReducer, resellerReducer }: RootState) => {
    return {
        user: userReducer.user,
        applicationSubName: systemReducer.applicationSubName,
        applicationImgUrl: systemReducer.applicationImgUrl,
        notificationCount: 0,
        selectedReseller: resellerReducer.selectedReseller,
        authorizedResellers: resellerReducer.authorizedResellers,
        resellerDetails: resellerReducer.resellerDetails,
        stage: systemReducer.stage.value,
    };
};

const mapDispatchToProps = { getResellerDetails, getAuthorizedResellers };

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(Navbar);
